import { useCallback, useEffect } from 'react';
import { connect } from 'redux-bundler-react';
import { useFormContext } from 'react-hook-form';
import { useJpa, useRequest } from '@stores';
import { MarkdownRenderer } from '@src/app-components/markdown-renderer/MarkdownRenderer';
import { generateInputName, QUESTION_TYPE } from './ddf.validation';
import {
  calculateQuestionFormIndex,
  getOtherProps,
  getQuestionVisibility,
  manageSectionVisibilityHelper,
  shouldHideForResponse,
} from './helpers';
import { JpaInputMap } from '@components/dynamic-form/inputMap';
import classNames from 'classnames';

const getAnswerValue = (answer) => {
  if (!answer) return undefined;

  return (
    answer.jpaShortAnswerText ??
    answer.jpaFullAnswerText ??
    answer.jpaAnswerDate ??
    answer.jpaAnswerNumber ??
    answer.jpaAnswerYesNo ??
    answer.hasJPAFileUploaded ??
    answer.jpaListValues
  );
};

const Question = connect(
  ({
    componentId,
    defaultValue,
    inputNameOverride,
    isInformation,
    isReadOnly,
    jpaFormDocuments,
    jpaFormID,
    onChange = () => {},
    onChangeProps = {},
    stepNo,
    questionData,
  }) => {
    const {
      allowsMultiselect,
      isFullSectionHide,
      isRequired,
      jpaLists,
      jpaParentQuestionID,
      jpaQuestionID,
      jpaQuestionTypeID,
      parentQuestionTree,
      responseForHide,
      question,
    } = questionData;

    const { clearAnswers, jpaContactAnswerSet, setQuestionText, setQuestionVisibility } = useJpa();

    const answerSet = useJpa((state) => state.answerSet);

    const { updateSection, sections, getSectionParentId, getChildrenHiddenState } = useRequest();
    const jpaBasicInfo = useJpa.getState().jpaBasicInfo;
    const { getValues } = useFormContext();

    const questionDocumentFiles = jpaFormDocuments?.filter(
      (file) => file?.isQuestionLocation && file?.jpaDocumentQuestionIDs?.includes(jpaQuestionID)
    );

    const isBasicContactInput = jpaQuestionTypeID === QUESTION_TYPE.basicContact;
    const isContactInput = jpaQuestionTypeID === QUESTION_TYPE.contact;
    const isDateInput = jpaQuestionTypeID === QUESTION_TYPE.date;
    const isFileInput = jpaQuestionTypeID === QUESTION_TYPE.file;
    const isLongAnswerInput = jpaQuestionTypeID === QUESTION_TYPE.longAnswer;
    const isNumberInput = jpaQuestionTypeID === QUESTION_TYPE.number;
    const isOptionInput = jpaQuestionTypeID === QUESTION_TYPE.option;
    const isShortAnswerInput = jpaQuestionTypeID === QUESTION_TYPE.shortAnswer;
    const isTableInput = jpaQuestionTypeID === QUESTION_TYPE.table;
    const isYesNoInput = jpaQuestionTypeID === QUESTION_TYPE.yesNo;

    const memoizedCalculateQuestionFormIndex = useCallback(
      () => calculateQuestionFormIndex(sections, jpaBasicInfo, stepNo),
      [sections, jpaBasicInfo, stepNo]
    );

    const manageSectionVisibility = useCallback(
      (value, targetValue) => {
        manageSectionVisibilityHelper({
          calculateQuestionFormIndex: memoizedCalculateQuestionFormIndex,
          clearAnswers,
          getChildrenHiddenState,
          getSectionParentId,
          isInformationHidden: sections[stepNo]?.hidden === true,
          jpaFormID,
          sections,
          targetValue,
          updateSection,
          value,
        });
      },
      [
        clearAnswers,
        getChildrenHiddenState,
        getSectionParentId,
        jpaFormID,
        memoizedCalculateQuestionFormIndex,
        sections,
        stepNo,
        updateSection,
      ]
    );

    const formValues = {
      ...getValues(),
      ...Object.values(answerSet?.[jpaFormID] || {}).reduce((acc, answer) => {
        acc[`${answer.jpaQuestionTypeID}-${answer.jpaQuestionID}`] = getAnswerValue(answer);
        return acc;
      }, {}),
    };

    const isQuestionVisible = getQuestionVisibility({
      formId: jpaFormID,
      formValues,
      parentQuestionTree,
    });

    const isContainingSectionHidden = sections[stepNo]?.hidden === true;

    useEffect(() => {
      const isVisible = !isContainingSectionHidden && (!jpaParentQuestionID || isQuestionVisible);

      setQuestionVisibility(jpaQuestionID, isVisible);
    }, [isContainingSectionHidden, isQuestionVisible, jpaParentQuestionID, jpaQuestionID, setQuestionVisibility]);

    const Input = JpaInputMap[allowsMultiselect ? QUESTION_TYPE.multiselect : jpaQuestionTypeID];
    const inputName = inputNameOverride || generateInputName(jpaQuestionTypeID, jpaQuestionID);

    const handleOnChange = useCallback(
      (e) => {
        let value;

        if (
          (isOptionInput && allowsMultiselect) ||
          isTableInput ||
          isYesNoInput ||
          isContactInput ||
          isBasicContactInput
        ) {
          value = e;
        } else {
          value = e.target.value;
        }

        if (isInformation && isFullSectionHide) {
          const targetValue = shouldHideForResponse(value, responseForHide);
          manageSectionVisibility(value, targetValue);
        }

        onChange?.(value, jpaQuestionID, jpaQuestionTypeID, jpaFormID, onChangeProps);
      },
      [
        allowsMultiselect,
        isBasicContactInput,
        isContactInput,
        isFullSectionHide,
        isInformation,
        isOptionInput,
        isTableInput,
        isYesNoInput,
        jpaFormID,
        jpaQuestionID,
        jpaQuestionTypeID,
        manageSectionVisibility,
        onChange,
        onChangeProps,
        responseForHide,
      ]
    );

    useEffect(() => {
      if (questionData && questionData.jpaQuestionID && questionData.question) {
        setQuestionText(questionData.jpaQuestionID, questionData.question);
      }
    }, [questionData, setQuestionText]);

    useEffect(() => {
      if (isInformation && isFullSectionHide && inputName) {
        const questionValue = getValues(inputName);
        const targetValue = shouldHideForResponse(questionValue, responseForHide);

        manageSectionVisibility(questionValue, targetValue);
      }
    }, [isInformation, isFullSectionHide, inputName, responseForHide, getValues, manageSectionVisibility]);

    const containerStyles = classNames('ddf-question', {
      'date-picker': isDateInput,
      'long-answer': isLongAnswerInput,
      'short-answer': isShortAnswerInput,
      'yes-no': isYesNoInput,
      file: isFileInput,
      number: isNumberInput,
      option: isOptionInput,
    });

    const otherProps = getOtherProps({
      allowsMultiselect,
      componentId,
      handleOnChange,
      isBasicContactInput,
      isContactInput,
      isDateInput,
      isFileInput,
      isOptionInput,
      isNumberInput,
      isReadOnly,
      isShortAnswerInput,
      isTableInput,
      jpaContactAnswerSet,
      jpaFormID,
      jpaLists,
      jpaQuestionID,
      onChange,
      onChangeProps,
      questionData,
    });

    if (jpaParentQuestionID && !isQuestionVisible) {
      return null;
    }

    return (
      <div className={containerStyles}>
        <Input
          defaultValue={defaultValue}
          id={inputName}
          label={<MarkdownRenderer displayInline text={question} />}
          fileDownload={questionDocumentFiles}
          name={inputName}
          onChange={handleOnChange}
          readOnly={isReadOnly}
          required={isRequired}
          tooltip={questionData?.helpInfo}
          {...otherProps}
        />
      </div>
    );
  }
);

export default Question;
